// When meetings happen (API sql/meeting-time.sql). A meeting is stored the way calendars store events: a first
// date ("Date"), a wall-clock "StartTime" in an IANA "TimeZone", a "DurationMinutes", and a repeat rule
// ("RecurrenceType": once / daily / weekly / biweekly / monthly, i.e. RRULE FREQ=DAILY/WEEKLY/MONTHLY; biweekly is FREQ=WEEKLY;INTERVAL=2). Occurrences are worked
// out on the meeting's own calendar (its zone), then shown in the viewer's local time. Meetings made before times
// existed have no StartTime/TimeZone: they work by date alone, like an all-day calendar event.

type Ymd = { y: number, m: number, d: number }; // m is 1-12

// One occurrence: its date on the meeting's calendar, and its start/end instants (null when no time is set)
export type Occurrence = { date: Ymd, start: Date | null, end: Date | null };

function parseYmd(s: string): Ymd {
    const [ y, m, d ] = s.split('-').map(n => parseInt(n));
    return { y, m, d };
}

function ymdKey(a: Ymd): number {
    return Date.UTC(a.y, a.m - 1, a.d);
}

function addDays(a: Ymd, days: number): Ymd {
    const t = new Date(Date.UTC(a.y, a.m - 1, a.d + days));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function daysInMonth(y: number, m: number): number {
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

// "YYYY-MM-DD" of the meeting's first date (the API sends the DATE column as a timestamp)
export function meetingStartDate(meeting: any): string {
    return new Date(meeting.Date).toISOString().split('T')[0];
}

export function browserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch(e) {
        return 'UTC';
    }
}

// Every IANA zone the browser knows (for the meeting form's picker), with the given ones guaranteed in
export function timeZoneOptions(...include: string[]): string[] {
    let zones: string[] = [];
    try {
        zones = (Intl as any).supportedValuesOf('timeZone');
    } catch(e) { /* older browsers: just the ones passed in */ }
    return [ ...new Set([ ...include.filter(z => z), ...zones ]) ].sort();
}

export function hasTime(meeting: any): boolean {
    return !!(meeting?.StartTime && meeting?.TimeZone);
}

// "HH:MM" from the TIME column ("09:00:00")
export function startTimeHM(meeting: any): string {
    return meeting?.StartTime ? String(meeting.StartTime).slice(0, 5) : '';
}

// The meeting form's start time choices: every 15 minutes as { value: "HH:MM", label: "9:15 AM" }, plus the
// given time when it's off that grid (a meeting saved before the picker was simplified keeps its own time).
// The list starts at 6:00 AM, with the overnight times (12:00 AM to 5:45 AM) at the end.
export function startTimeOptions(current: string): { value: string, label: string }[] {
    const values = [];
    for(let m = 0; m < 24 * 60; m += 15) {
        values.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    }
    if(current && !values.includes(current)) {
        values.push(current);
    }
    const fromSix = (v: string) => (v < '06:00' ? '1' : '0') + v;
    values.sort((a, b) => fromSix(a).localeCompare(fromSix(b)));
    return values.map(value => {
        const [ h, mi ] = value.split(':').map(n => parseInt(n));
        return { value, label: `${h % 12 || 12}:${String(mi).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}` };
    });
}

// The wall-clock date and time an instant falls on in a zone
function zoneParts(ms: number, timeZone: string) {
    const parts: any = {};
    for(const p of new Intl.DateTimeFormat('en-US', {
        timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
    }).formatToParts(new Date(ms))) {
        parts[p.type] = parseInt(p.value);
    }
    return { y: parts.year, m: parts.month, d: parts.day, h: parts.hour % 24, mi: parts.minute, s: parts.second };
}

// How far a zone is ahead of UTC at an instant
function offsetMs(ms: number, timeZone: string): number {
    const p = zoneParts(ms, timeZone);
    return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000;
}

// The instant a wall-clock date and time happens in a zone (Postgres: ("Date" + "StartTime") AT TIME ZONE "TimeZone")
export function zonedInstant(date: Ymd, time: string, timeZone: string): Date {
    const [ h, mi ] = time.split(':').map(n => parseInt(n));
    const guess = Date.UTC(date.y, date.m - 1, date.d, h, mi);
    let t = guess - offsetMs(guess, timeZone);
    // Re-check across a daylight saving change
    const second = offsetMs(t, timeZone);
    if(guess - second != t) {
        t = guess - second;
    }
    return new Date(t);
}

// Today on the meeting's calendar (its zone), or the viewer's when it has no time
function today(meeting: any, now: Date): Ymd {
    if(hasTime(meeting)) {
        const p = zoneParts(now.getTime(), meeting.TimeZone);
        return { y: p.y, m: p.m, d: p.d };
    }
    return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
}

// The k-th date in the series, or null for a month that doesn't have the day (a meeting on the 31st skips
// 30-day months, as calendars do)
function occurrenceDate(meeting: any, start: Ymd, k: number): Ymd | null {
    switch(meeting.RecurrenceType) {
        case 'daily':
            return addDays(start, k);
        case 'weekly':
            return addDays(start, k * 7);
        case 'biweekly':
            return addDays(start, k * 14);
        case 'monthly': {
            const total = start.m - 1 + k;
            const y = start.y + Math.floor(total / 12), m = total % 12 + 1;
            return start.d <= daysInMonth(y, m) ? { y, m, d: start.d } : null;
        }
        default:
            return k == 0 ? start : null;
    }
}

// The first n dates in the series on or after a date
function datesFrom(meeting: any, from: Ymd, n: number): Ymd[] {
    const start = parseYmd(meetingStartDate(meeting));
    const days = Math.round((ymdKey(from) - ymdKey(start)) / 86400000);
    let k = 0;
    if(days > 0) {
        k = meeting.RecurrenceType == 'daily' ? days - 1
            : meeting.RecurrenceType == 'weekly' ? Math.floor(days / 7) - 1
            : meeting.RecurrenceType == 'biweekly' ? Math.floor(days / 14) - 1
            : meeting.RecurrenceType == 'monthly' ? (from.y - start.y) * 12 + from.m - start.m - 1
            : 0;
    }
    const out: Ymd[] = [];
    for(let i = Math.max(0, k); out.length < n && i < k + 500; i++) {
        const d = occurrenceDate(meeting, start, i);
        if(d && ymdKey(d) >= ymdKey(from)) {
            out.push(d);
        }
    }
    return out;
}

function occurrence(meeting: any, date: Ymd): Occurrence {
    if(!hasTime(meeting)) {
        return { date, start: null, end: null };
    }
    const start = zonedInstant(date, startTimeHM(meeting), meeting.TimeZone);
    return { date, start, end: new Date(start.getTime() + (meeting.DurationMinutes || 0) * 60000) };
}

// The meeting's next occurrence: the first one that hasn't ended yet (one that's under way still counts), or,
// with no time set, the first that's today or later. A one-time meeting is always its own date, even once past.
export function nextOccurrence(meeting: any, now: Date = new Date()): Occurrence {
    const start = parseYmd(meetingStartDate(meeting));
    if(meeting.RecurrenceType == 'once') {
        return occurrence(meeting, start);
    }
    for(const date of datesFrom(meeting, today(meeting, now), 3)) {
        const o = occurrence(meeting, date);
        if(!o.end || o.end.getTime() > now.getTime()) {
            return o;
        }
    }
    return occurrence(meeting, start);
}

// The n occurrences after one (the "Upcoming" chips)
export function occurrencesAfter(meeting: any, after: Occurrence, n: number): Occurrence[] {
    if(meeting.RecurrenceType == 'once') {
        return [];
    }
    return datesFrom(meeting, addDays(after.date, 1), n).map(date => occurrence(meeting, date));
}

// Every occurrence that starts in [from, to) for the viewer (Meetings Home's week), ended ones included, soonest first.
// A meeting with no time set counts by its date, as local midnight.
export function occurrencesInRange(meeting: any, from: Date, to: Date): Occurrence[] {
    // Start a day early on the meeting's calendar, so a zone ahead of or behind the viewer's isn't cut off
    const start = addDays(today(meeting, from), -1);
    const days = Math.ceil((to.getTime() - from.getTime()) / 86400000) + 2;
    const count = meeting.RecurrenceType == 'daily' ? days + 1 : meeting.RecurrenceType == 'weekly' ? Math.ceil(days / 7) + 1
        : meeting.RecurrenceType == 'biweekly' ? Math.ceil(days / 14) + 1 : 2;
    return datesFrom(meeting, start, count)
        .map(date => occurrence(meeting, date))
        .filter(o => {
            const t = occurrenceLocal(o).getTime();
            return t >= from.getTime() && t < to.getTime();
        });
}

// When it happens for the viewer: the start instant, or local midnight of its date when no time is set
export function occurrenceLocal(o: Occurrence): Date {
    return o.start || new Date(o.date.y, o.date.m - 1, o.date.d);
}

// For sorting meetings soonest first
export function occurrenceSortKey(o: Occurrence): number {
    return occurrenceLocal(o).getTime();
}

// "CT", "ET"... (falls back to "CDT"-style, then the zone name)
export function zoneLabel(timeZone: string, at: Date = new Date()): string {
    for(const timeZoneName of [ 'shortGeneric', 'short' ]) {
        try {
            const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName } as any).formatToParts(at).find(p => p.type == 'timeZoneName');
            if(part?.value) {
                return part.value;
            }
        } catch(e) { /* try the next */ }
    }
    return timeZone;
}

// "10:00 AM", in the viewer's local time, adding the meeting's own time when its zone is different right now:
// "10:00 AM (9:00 AM CT)". Empty when no time is set.
export function occurrenceTimeLabel(meeting: any, o: Occurrence): string {
    if(!o.start) {
        return '';
    }
    const opts: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
    const local = o.start.toLocaleTimeString([], opts);
    if(offsetMs(o.start.getTime(), meeting.TimeZone) == offsetMs(o.start.getTime(), browserTimeZone())) {
        return local;
    }
    return `${local} (${o.start.toLocaleTimeString([], { ...opts, timeZone: meeting.TimeZone })} ${zoneLabel(meeting.TimeZone, o.start)})`;
}

// "45 min", "1 hr", "1 hr 30 min"
export function durationLabel(minutes: number): string {
    const m = Math.max(0, Math.round(minutes || 0));
    const h = Math.floor(m / 60), rest = m % 60;
    if(!h) {
        return `${rest} min`;
    }
    return rest ? `${h} hr ${rest} min` : `${h} hr`;
}
